import React from 'react';

/**
 * Accuracy by season, drawn as inline SVG.
 *
 * No charting dependency: five points and a line do not justify pulling a
 * library into the bundle, and hand-drawing it keeps the axis honest - it
 * starts at 50%, a coin flip, rather than at the lowest value, which would
 * exaggerate the slope.
 *
 * Colours go through Tailwind classes rather than fill/stroke attributes so
 * they follow the theme. Colours use the CSS variable directly, since a
 * stop-color cannot take a utility class.
 */
const AccuracyChart = ({ data, height = 180 }) => {
  const width = 420;
  // Right padding carries the last point's value label, which is centred on it
  // and would otherwise run past the viewBox edge.
  const padding = { top: 18, right: 30, bottom: 28, left: 40 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;

  const yMin = 0.5;   // a coin flip - the only meaningful floor
  const yMax = 0.75;
  const x = (index) => padding.left + (index / (data.length - 1)) * plotWidth;
  const y = (value) => padding.top + (1 - (value - yMin) / (yMax - yMin)) * plotHeight;

  const line = data.map((row, index) => `${index === 0 ? 'M' : 'L'} ${x(index)} ${y(row.accuracy)}`).join(' ');

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="h-auto w-full"
      role="img"
      aria-label={
        `Backtest accuracy by season: ${data.map((r) => `${r.season} ${(r.accuracy * 100).toFixed(1)}%`).join(', ')}`
      }
    >
      {[0.5, 0.6, 0.7].map((tick) => (
        <g key={tick}>
          <line x1={padding.left} x2={width - padding.right} y1={y(tick)} y2={y(tick)}
                className="stroke-edge" strokeWidth="1" />
          <text x={padding.left - 8} y={y(tick) + 4} textAnchor="end" fontSize="10" className="fill-content-muted">
            {Math.round(tick * 100)}%
          </text>
        </g>
      ))}

      {/*
        * No area fill. It was a gradient wash under the line carrying no
        * information - the line, the points and the printed values already say
        * everything the chart has to say, and a fade to nothing invites the eye
        * to read area as meaning.
        */}
      <path d={line} fill="none" className="stroke-accent" strokeWidth="2.5" strokeLinejoin="round" />

      {data.map((row, index) => (
        <g key={row.season}>
          <circle cx={x(index)} cy={y(row.accuracy)} r="4" className="fill-surface stroke-accent" strokeWidth="2.5" />
          <text x={x(index)} y={y(row.accuracy) - 12} textAnchor="middle" fontSize="11" className="fill-content" fontWeight="600">
            {(row.accuracy * 100).toFixed(1)}%
          </text>
          <text x={x(index)} y={height - 8} textAnchor="middle" fontSize="10" className="fill-content-muted">
            {row.season}
          </text>
        </g>
      ))}
    </svg>
  );
};

export default AccuracyChart;
